/**
 *
 * Profile
 *
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Lock, Mail, Smartphone, ChevronRight } from 'lucide-react';
import { ProfileTab } from './tabs/ProfileTab';
import { PasswordTab } from './tabs/PasswordTab';
import { EmailTab } from './tabs/EmailTab';
import { TwoFATab } from './tabs/TwoFATab';

// Sidebar menu items
const sidebarItems = [
  {
    id: 'profile',
    label: 'Profile',
    icon: User,
    description: 'Personal information and avatar',
  },
  {
    id: 'password',
    label: 'Change Password',
    icon: Lock,
    description: 'Update your account password',
  },
  {
    id: 'email',
    label: 'Change Email',
    icon: Mail,
    description: 'Update your email address',
  },
  {
    id: '2fa',
    label: 'Two-Factor Auth',
    icon: Smartphone,
    description: 'Secure your account with 2FA',
  },
];

export default function Profile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  // Render the appropriate component based on active tab
  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab />;
      case 'password':
        return <PasswordTab />;
      case 'email':
        return <EmailTab />;
      case '2fa':
        return <TwoFATab />;
      default:
        return <ProfileTab />;
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Profile Settings
          </h2>
          <nav className="space-y-1">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === item.id
                      ? 'bg-primary-50 text-primary border-r-2 border-primary'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {item.description}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                {sidebarItems.find(item => item.id === activeTab)?.label}
              </h1>
              <p className="text-gray-600 mt-1">
                {sidebarItems.find(item => item.id === activeTab)?.description}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">{renderActiveComponent()}</div>
        </div>
      </div>
    </div>
  );
}
